import { SearchType } from '@zoopla/listing-search-context';
import {
    FormValues,
    getDuration,
    getFormValuesFromQuery,
    getQ,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import { NextRouter, useRouter } from 'next/router';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    createContext,
    SetStateAction,
    Dispatch,
    useMemo,
    memo,
    DependencyList
} from 'react';
import {
    formatIdentifier,
    getResultsPageUrl
} from '@zoopla/listing-search-utils';
import { getQueryParams } from '../utils/getQueryParams';
import { coerceQueryParameter } from '../utils/coerceQueryParameter';
import { ParsedUrlQuery } from 'querystring';
import debounce from 'lodash.debounce';
import { useGetSearchLocationFromPath } from '@zoopla/listing-search/get-search-location-from-path';

export type SearchBarProps = {
    showRadius?: boolean;
    isNewHomesDevelopersSearch?: boolean;
    type?: string;
    tracking?: {
        search?: () => void;
        emptySearch?: () => void;
    };
    automaticUpdate?: boolean;
};

const getIdentifier = (query: NextRouter['query']) =>
    formatIdentifier(getSearchPath(query));

const getLocation = (query: NextRouter['query']) => {
    const q = getQ(query);
    const coercedQ = coerceQueryParameter(q);
    if (coercedQ !== '') return coercedQ;
    return;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const defaultQuery: ParsedUrlQuery = {};
const formValueDefault = getFormValuesFromQuery({
    query: defaultQuery,
    pathname: ''
});

type SearchBarContextDefault = {
    formValue: FormValues;
    handleSearchProperties?: (props?: { forceUpdate: boolean }) => void;
    isForSaleSearch: boolean;
    isLoading: boolean;
    isTravelTimeSearch: boolean;
    hasError: boolean;
    showRadius: boolean;
    updateFormValue?: Dispatch<SetStateAction<FormValues>>;
};

export const SearchBarContextDefault = {
    formValue: formValueDefault,
    handleSearchProperties: undefined,
    isForSaleSearch: true,
    isLoading: false,
    isTravelTimeSearch: false,
    hasError: false,
    showRadius: true,
    updateFormValue: undefined
};

export const SearchBarContext = createContext<SearchBarContextDefault>(
    SearchBarContextDefault
);

const createUrlString = (urlObject: {
    pathname: string;
    query: ConstructorParameters<typeof URLSearchParams>[0];
}) => {
    const queryString = new URLSearchParams(urlObject.query).toString();

    return `${urlObject.pathname}${queryString ? '?' + queryString : ''}`;
};

const getActiveFilters = (allFilters: FormValues) => {
    const filters: (keyof FormValues)[] = [
        'added',
        'available_from',
        'beds_max',
        'beds_min',
        'bills_included',
        'chain_free',
        'duration',
        'feature',
        'furnished_state',
        'include_rented',
        'include_sold',
        'is_auction',
        'is_retirement_home',
        'is_shared_accommodation',
        'is_shared_ownership',
        'keywords',
        'new_homes',
        'pets_allowed',
        'polyenc',
        'price_frequency',
        'price_max',
        'price_min',
        'property_sub_type',
        'property_type',
        'radius',
        'reduced_price_only',
        'results_sort',
        'transport_type',
        'q'
    ];

    return filters.reduce((acc, curr) => {
        if (curr === 'property_type' && allFilters[curr] !== '') {
            return {
                ...acc,
                [curr]: allFilters[curr] === 'property' ? '' : allFilters[curr]
            };
        }
        const hasVal = (v: string | string[]) =>
            Array.isArray(v) ? v.length > 0 && v[0] !== '' : v !== '';
        return {
            ...acc,
            ...(hasVal(allFilters[curr]) && {
                [curr]: allFilters[curr]
            })
        };
    }, {});
};

const getSearchTypeFromPath = (path: string) => {
    if (path.includes('for-sale')) {
        return 'for-sale';
    } else if (path.includes('new-homes')) {
        return 'new-homes';
    } else if (path.includes('to-rent')) {
        return 'to-rent';
    } else {
        return '';
    }
};

function cleanUndefinedValues<T>(obj: T): T {
    for (const key in obj) {
        if (!obj[key]) {
            delete obj[key];
        }
    }
    return obj;
}

function useEffectAfterMount<T extends DependencyList | undefined>(
    effect: () => void,
    dependencies: T
): void {
    const isFirstMount = useRef(true);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
        } else {
            effect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
}

export const SearchBar = memo<React.PropsWithChildren<SearchBarProps>>(
    ({
        children,
        showRadius = true,
        isNewHomesDevelopersSearch,
        type,
        tracking,
        automaticUpdate
    }) => {
        const { query, push, pathname, prefetch, asPath } = useRouter();
        const initialFormValue = getFormValuesFromQuery({ pathname, query });
        const initialSearchLocation = useGetSearchLocationFromPath();
        const [formValue, updateFormValue] = useState<FormValues>({
            ...initialFormValue,
            q: coerceQueryParameter(
                initialFormValue.q || initialSearchLocation.label
            )
        });
        const [isLoading, setLoading] = useState(false);
        const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
            useState(false);
        const originalQuery = useRef(getLocation(query));
        const identifier = useRef(getIdentifier(query));
        const prefetchUrl = useRef('');

        const searchType = type || getSearchTypeFromPath(asPath);
        const viewType = pathname.includes('/map/') ? 'map' : 'list';

        useEffect(() => {
            if (isNewHomesDevelopersSearch) {
                prefetchUrl.current = getNewHomesDevelopersQuery();

                prefetch(prefetchUrl.current);
            } else {
                getResultsPageUrl(getSearchParams()).then((lsrpUrl) => {
                    if (lsrpUrl) {
                        prefetchUrl.current = lsrpUrl;
                        prefetch(prefetchUrl.current);
                    }
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formValue, query]);

        const hasSearchUpdated = useCallback(
            () => prefetchUrl.current !== asPath,
            [prefetchUrl, asPath]
        );

        const getSearchParams = useCallback(() => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const defaultQueryParams = getQueryParams(searchType as SearchType);

            const params = cleanUndefinedValues({
                ...defaultQueryParams,
                ...getActiveFilters(formValue),
                ...(searchType === 'new-homes' ? { new_homes: 'only' } : {}),
                q: formValue.q,
                orig_q: originalQuery.current,
                polyenc: formValue.polyenc,
                geo_autocomplete_identifier:
                    formValue.geo_autocomplete_identifier,
                identifier: identifier.current,
                view_type: viewType
            });

            return params;
        }, [formValue, searchType, viewType]);

        const getNewHomesDevelopersQuery = useCallback(() => {
            const query = new URLSearchParams();
            if (formValue.beds_max)
                query.set('beds_max', coerceQueryParameter(formValue.beds_max));
            if (formValue.beds_min)
                query.set('beds_min', coerceQueryParameter(formValue.beds_min));
            if (formValue.price_min)
                query.set(
                    'price_min',
                    coerceQueryParameter(formValue.price_min)
                );
            if (formValue.price_max)
                query.set(
                    'price_max',
                    coerceQueryParameter(formValue.price_max)
                );
            query.set(
                'property_type',
                coerceQueryParameter(formValue.property_type)
            );

            if (formValue.q) {
                query.set('q', coerceQueryParameter(formValue.q));
            }

            const searchParams = query.toString();

            let searchPath;

            if (formValue.geo_autocomplete_identifier) {
                searchPath = `/new-homes/developers/${formValue.geo_autocomplete_identifier}/`;
            } else {
                searchPath = '/new-homes/developers/';
            }

            return createUrlString({
                pathname: searchPath,
                query: searchParams || undefined
            });
        }, [
            formValue.beds_max,
            formValue.beds_min,
            formValue.geo_autocomplete_identifier,
            formValue.price_max,
            formValue.price_min,
            formValue.property_type,
            formValue.q
        ]);

        const handleSearchProperties = useMemo(
            () =>
                debounce(
                    async (props?: { forceUpdate: boolean } | undefined) => {
                        if (
                            hasSearchUpdated() === false &&
                            !props?.forceUpdate
                        ) {
                            return;
                        }

                        if (!formValue.q) {
                            setHasSubmittedLocationSearch(true);
                            tracking?.emptySearch?.();
                            return;
                        }

                        setHasSubmittedLocationSearch(false);

                        tracking?.search?.();

                        setLoading(true);

                        if (isNewHomesDevelopersSearch) {
                            if (prefetchUrl.current) {
                                await push(prefetchUrl.current);
                            } else {
                                await push(getNewHomesDevelopersQuery());
                            }
                        } else {
                            if (prefetchUrl.current) {
                                await push(prefetchUrl.current);
                            } else {
                                await push({
                                    pathname: '/search/',
                                    query: getSearchParams()
                                });
                            }
                        }

                        setLoading(false);
                    },
                    250,
                    {
                        trailing: true
                    }
                ),
            [
                formValue.q,
                getNewHomesDevelopersQuery,
                getSearchParams,
                isNewHomesDevelopersSearch,
                push,
                tracking,
                hasSearchUpdated
            ]
        );

        // Updates search when the user selects an option from the select dropdown
        useEffectAfterMount(() => {
            if (
                !automaticUpdate ||
                !hasSearchUpdated() ||
                formValue.geo_autocomplete_identifier === ''
            ) {
                return;
            }

            handleSearchProperties();
        }, [formValue.geo_autocomplete_identifier]);

        const memoizedContextValue = useMemo(() => {
            return {
                handleSearchProperties,
                hasError: hasSubmittedLocationSearch && !formValue.q,
                isTravelTimeSearch: Boolean(
                    getDuration(query) && getTransportType(query)
                ),
                showRadius,
                formValue,
                updateFormValue,
                isForSaleSearch:
                    searchType === 'for-sale' || searchType === 'new-homes',
                isLoading
            };
        }, [
            handleSearchProperties,
            hasSubmittedLocationSearch,
            formValue,
            query,
            showRadius,
            searchType,
            isLoading
        ]);

        return (
            <SearchBarContext.Provider value={memoizedContextValue}>
                {children}
            </SearchBarContext.Provider>
        );
    }
);
